import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  IconButton,
  Chip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  MarkEmailRead as MarkReadIcon,
  MarkEmailUnread as MarkUnreadIcon
} from '@mui/icons-material';

// Services
import { 
  getNotifications, 
  markNotificationAsRead, 
  markNotificationAsUnread,
  deleteNotification,
  markAllAsRead
} from '../services/notificationService';
import { useLanguage } from '../contexts/LanguageContext';
import PiedTableau from '../components/commun/PiedTableau';
import LigneNotification, { fondNonLue } from '../components/notifications/LigneNotification';

// Les labels seront traduits dynamiquement dans le composant
const notificationTypes = [
  { value: 'all', icon: NotificationsIcon },
  { value: 'low_stock', icon: WarningIcon },
  { value: 'out_of_stock', icon: WarningIcon },
  { value: 'order_received', icon: CheckCircleIcon },
  { value: 'info', icon: InfoIcon }
];

function Notifications({ showSnackbar }) {
  const { t } = useLanguage();
  // ⚠️ CET ÉCRAN NE MET PLUS AUCUNE DATE EN FORME : `LigneNotification` s'en
  // charge, pour la page comme pour le panneau. *Un import qui survit à son
  // dernier usage fait croire à une dépendance qui n'existe plus.*
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  // 🔴 LA PAGINATION ET LES COMPTEURS VIENNENT DU SERVEUR. L'écran filtrait et
  // comptait en JavaScript sur les 20 premières notifications que la route sert
  // par défaut : au-delà, il en perdait sans le dire, et ses quatre cartes
  // annonçaient le compte de la PAGE comme un total. La base de développement en
  // porte 6 — *le décor le cachait, et l'aurait caché jusqu'à la vingt et unième.*
  const [page, setPage] = useState(1);
  const [parPage, setParPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [compteurs, setCompteurs] = useState(null);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      // ⚠️ LES FILTRES PARTENT AU SERVEUR, ils ne sont plus rejoués ici : la
      // route sert `type`, `is_read` et désormais `search`. Filtrer côté client
      // une liste paginée rendrait « 0 résultat » sur un terme présent page 2.
      const data = await getNotifications({
        page,
        per_page: parPage,
        ...(searchTerm ? { search: searchTerm } : {}),
        ...(typeFilter !== 'all' ? { type: typeFilter } : {}),
        ...(statusFilter !== 'all' ? { is_read: statusFilter === 'read' } : {}),
      });
      setNotifications(data.notifications || []);
      setTotal(data.total ?? 0);
      setPages(data.pages || 1);
      setCompteurs(data.compteurs || null);
    } catch (error) {
      showSnackbar(t('notifications.loadError'), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, parPage, searchTerm, typeFilter, statusFilter]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      showSnackbar(t('notifications.markedAsRead'), 'success');
      loadNotifications();
    } catch (error) {
      showSnackbar(t('notifications.markingError'), 'error');
    }
  };

  const handleMarkAsUnread = async (notificationId) => {
    try {
      await markNotificationAsUnread(notificationId);
      showSnackbar(t('notifications.markedAsUnread'), 'success');
      loadNotifications();
    } catch (error) {
      showSnackbar(t('notifications.markingError'), 'error');
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      showSnackbar(t('notifications.deleted'), 'success');
      loadNotifications();
    } catch (error) {
      showSnackbar(t('notifications.deleteError'), 'error');
    }
  };

  // 🔴 IL ANNONCE CE QU'IL VA PRODUIRE AVANT DE LE FAIRE — doctrine du dialogue
  // de réception (RU5-c) et de « Tout commander » (RU6). Une écriture de MASSE
  // qui s'exécute sur un clic sans dire combien de lignes elle touche oblige à
  // découvrir son effet après coup.
  // ⚠️ Le nombre annoncé vient du COMPTEUR SERVI, pas de la page : c'est celui
  // que le serveur va réellement mettre à jour.
  const [confirmationLecture, setConfirmationLecture] = useState(false);

  const handleMarkAllAsRead = async () => {
    try {
      const r = await markAllAsRead();
      setConfirmationLecture(false);
      // ⚠️ ON REDIT LE NOMBRE APRÈS COUP, avec le chiffre que le SERVEUR rend
      // (`updated_count`) — jamais celui qu'on avait annoncé. Répéter sa propre
      // annonce ne vérifie rien ; c'est l'écart entre les deux qui informe.
      showSnackbar(t('notifications.allMarkedAsReadCount',
                     { count: r?.updated_count ?? 0 }), 'success');
      loadNotifications();
    } catch (error) {
      setConfirmationLecture(false);
      showSnackbar(t('notifications.markingError'), 'error');
    }
  };

  // 🔴 PLUS AUCUN FILTRAGE CÔTÉ CLIENT. Les trois filtres sont servis par la
  // route ; les rejouer ici sur une page les rendrait faux dès la deuxième.
  // La liste affichée EST celle que le serveur a filtrée.
  const filteredNotifications = notifications;

  const getNotificationIcon = (type) => {
    const typeObj = notificationTypes.find(typeItem => typeItem.value === type);
    return typeObj ? typeObj.icon : NotificationsIcon;
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'low_stock':
      case 'out_of_stock':
        return 'error';
      case 'order_received':
        return 'success';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  // 🔴 LES DEUX TRADUCTEURS DE PROSE ONT ÉTÉ RETIRÉS AU LOT RU7-a′ — 74 lignes.
  // `translateNotificationTitle` vit maintenant dans `LigneNotification`, PARTAGÉE
  // avec le panneau de la cloche : deux mises en forme de la même notification
  // finiraient par dire deux choses.
  // ⚠️ Et `translateNotificationMessage` disparaît sans remplaçant : elle RELISAIT
  // le message stocké à l'expression régulière — trois motifs, deux replis, et un
  // abandon silencieux si rien ne correspondait — pour en extraire le stock et le
  // seuil, puis réémettait une phrase traduite. Le serveur sert désormais
  // `contexte` = `{stock, seuil}`, reconnu par une ÉGALITÉ DE FORME.
  // *Analyser sa propre prose pour la retraduire, c'est ranger une donnée dans
  // une phrase puis payer pour l'en ressortir.*

  // 🔴 LES QUATRE CHIFFRES VIENNENT DU SERVEUR, ils ne sont plus recalculés sur
  // la liste chargée. Recalculés, ils comptaient la PAGE en se présentant comme
  // des totaux. ⚠️ Et ils portent sur TOUT le stock, **pas sur le filtre
  // courant** : ce sont des repères stables. *Un compteur qui changerait à chaque
  // frappe ne dirait plus « il vous reste 4 non lues ».*
  const unreadCount = compteurs?.non_lues ?? 0;
  const totalCount = compteurs?.total ?? 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('notifications.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<MarkReadIcon />}
          onClick={() => setConfirmationLecture(true)}
          disabled={unreadCount === 0}
        >
          {t('notifications.markAllAsRead')}
        </Button>
      </Box>

      {/* Statistiques */}
      {/* 🔴 DEUX CARTES, PLUS QUATRE — et les deux retirées étaient DÉTERMINÉES
          par celles qui restent. Mesuré à la recette :
            · « Ruptures » comptait les notifications de type `out_of_stock`, un
              type que RIEN NE PRODUIT — la carte était structurellement à zéro,
              pour toujours ;
            · « Stock Bas » comptait les `low_stock` NON LUES, donc — `low_stock`
              étant le seul type produit — exactement « Non lues ».
          ⚠️ L'incohérence vue à l'écran (« Stock Bas 0 » au-dessus de trois
          lignes portant la pastille « Stock Bas ») venait de là : **le compteur
          était juste, son libellé mentait.** Même leçon que « Reste à payer
          (hors annulées) » au lot RU5-e — *un chiffre dont on ignore l'assiette
          ne peut pas être contredit.*
          🔴 ET ON NE L'A PAS CORRIGÉ EN RALLONGEANT LE LIBELLÉ : nommer
          honnêtement deux cartes qui affichent toujours le même nombre, et une
          troisième qui ne peut jamais bouger, aurait rendu le mensonge lisible
          sans le retirer. *Un compteur qui ne peut pas être non nul rassure à
          tort ; un compteur déterminé par son voisin finira par le contredire.* */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                {t('notifications.totalNotifications')}
              </Typography>
              <Typography variant="h4">
                {totalCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                {t('notifications.unread')}
              </Typography>
              {/* La couleur est DÉCLARÉE dans les deux cas — garde Q de RU4-c′ :
                  un jeton par défaut n'est pas un oubli. */}
              <Typography variant="h4"
                          sx={{ color: unreadCount > 0 ? 'primary.main' : 'text.primary' }}>
                {unreadCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Filtres */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            label={t('common.search')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ minWidth: 300 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('notifications.typeLabel')}</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              label={t('notifications.typeLabel')}
            >
              {notificationTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {t(`notifications.type.${type.value}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('notifications.status')}</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              label={t('notifications.status')}
            >
              <MenuItem value="all">{t('notifications.all')}</MenuItem>
              <MenuItem value="unread">{t('notifications.unread')}</MenuItem>
              <MenuItem value="read">{t('notifications.read')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Liste des notifications */}
      <Paper>
        {(!filteredNotifications || filteredNotifications.length === 0) ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <Typography variant="h6" color="textSecondary">
              {t('notifications.noNotificationsFound')}
            </Typography>
          </Box>
        ) : (
          <List>
            {filteredNotifications.map((notification, index) => {
              const IconComponent = getNotificationIcon(notification.type);
              const color = getNotificationColor(notification.type);
              
              return (
                <React.Fragment key={notification.id}>
                  {/* 🔴 TROIS ZONES RÉSERVÉES, PLUS AUCUNE SUPERPOSITION.
                      `ListItemSecondaryAction` est positionné en ABSOLU par MUI
                      (`position: absolute; right: 16px`) : il ne prend aucune
                      place dans le flux, donc le contenu passe DESSOUS. Les
                      pastilles, poussées à droite du texte, se retrouvaient sous
                      les icônes d'action — et « Stock Bas » devenait illisible.
                      ⚠️ *Un élément hors du flux ne se corrige pas en poussant
                      ce qui l'entoure : on le remet dans le flux.* La ligne est
                      désormais un `flex` à trois zones — icône, contenu qui
                      grandit, actions qui ne rétrécissent pas.
                      ⚠️ ET LES PASTILLES PASSENT SOUS LE TEXTE, à gauche : elles
                      qualifient la notification, elles ne se comparent pas d'une
                      ligne à l'autre. À droite, elles disputaient une zone à des
                      boutons — même geste qu'au tableau des fournisseurs, où
                      trois colonnes de contact sont devenues une fiche. */}
                  <ListItem
                    disableGutters
                    sx={{
                      px: 2,
                      alignItems: 'flex-start',
                      gap: 1.5,
                      ...fondNonLue(!notification.is_read),
                      '&:hover': { backgroundColor: 'action.selected' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 'auto', mt: 0.5 }}>
                      <IconComponent color={color} />
                    </ListItemIcon>

                    {/* La zone qui GRANDIT. `minWidth: 0` est indispensable :
                        sans lui, un enfant flex refuse de passer sous sa largeur
                        de contenu et déborde au lieu de se couper. */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <LigneNotification notification={notification} />
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                        <Chip
                          label={t(`notifications.type.${notification.type}`) || notification.type}
                          size="small"
                          color={color}
                          variant="outlined"
                        />
                        {!notification.is_read && (
                          <Chip
                            label={t('notifications.new')}
                            size="small"
                            color="primary"
                          />
                        )}
                      </Box>
                    </Box>

                    {/* La zone RÉSERVÉE des actions : `flexShrink: 0`, donc elle
                        ne cède jamais sa place au texte — et le texte ne passe
                        jamais dessous, puisqu'elle est dans le flux. */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, mt: 0.25 }}>
                      {notification.is_read ? (
                        <IconButton
                          size="small"
                          onClick={() => handleMarkAsUnread(notification.id)}
                          color="primary"
                          aria-label={t('notifications.markAsUnread')}
                        >
                          <MarkUnreadIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleMarkAsRead(notification.id)}
                          color="success"
                          aria-label={t('notifications.markAsRead')}
                        >
                          <MarkReadIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(notification.id)}
                        color="error"
                        aria-label={t('common.delete')}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItem>
                  {index < (filteredNotifications?.length || 0) - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>

      {/* 🔴 LE PIED DE TABLEAU, ET IL EST NEUF : l'écran n'en avait aucun alors
          que la route pagine depuis toujours. Au-delà de 20 notifications, il en
          perdait sans le moindre signe. Le total annoncé est celui de la
          COLLECTION filtrée, pas de la page. */}
      <PiedTableau
        total={total}
        page={page}
        pages={pages}
        parPage={parPage}
        onPage={setPage}
        onParPage={(n) => { setParPage(n); setPage(1); }}
        libelleTotal={`${total} ${t('notifications.title').toLowerCase()}`}
        libelleParPage={t('common.perPage')}
      />

      {/* 🔴 L'ANNONCE AVANT L'ÉCRITURE DE MASSE. Le bouton ouvre ce dialogue au
          lieu d'agir : il dit COMBIEN de notifications vont changer d'état, et
          ce nombre vient du compteur SERVI — celui que le serveur va toucher.
          ⚠️ Il dit aussi ce que le geste NE fait pas : rien n'est supprimé. */}
      <Dialog open={confirmationLecture} onClose={() => setConfirmationLecture(false)}
              maxWidth="xs" fullWidth>
        <DialogTitle>{t('notifications.markAllTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('notifications.markAllSummary', { count: unreadCount })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmationLecture(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleMarkAllAsRead}>
            {t('notifications.markAllConfirm')}
          </Button>
        </DialogActions>
      </Dialog>


      {/* 🔴 LES DEUX BANDEAUX D'ALERTE ONT ÉTÉ RETIRÉS AVEC LEURS COMPTEURS.
          Ils redisaient en pleine largeur ce que les cartes affichaient déjà —
          « N produits en rupture », « N produits en stock bas » — à partir des
          deux chiffres qui viennent d'être mesurés faux : l'un structurellement
          nul, l'autre égal aux non lues.
          ⚠️ Et ils parlaient de PRODUITS en comptant des NOTIFICATIONS : *un
          libellé qui nomme une chose et compte une autre est un défaut qui
          survit à toutes les relectures*, parce que le nombre, lui, est
          plausible. La vraie mesure des ruptures vit au tableau de bord, sur la
          bonne assiette — les produits. */}
    </Box>
  );
}

export default Notifications; 